import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import EventIcon from '@mui/icons-material/Event';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { format } from 'date-fns';
import { lt } from 'date-fns/locale';
import {
  getUserFutureReservations,
  getUserPastReservations,
  cancelReservation,
  LOCATIONS,
  getTrainingPricePerHourWithPricing,
} from '../utils/firebaseUtils';
import { useAuth } from '../contexts/AuthContext';
import { vilniusToCalendarUTC } from '../utils/dateUtils';

const locationLabel = (r) => {
  if (r.location === LOCATIONS.SEB_ARENA) return 'Seb Arena';
  if (r.location === LOCATIONS.VRM_COURTS) return 'VRM';
  return r.location || '—';
};

const courtLabel = (r) => {
  const c = (r.courtNo || r.sebCourtName || '').toString().trim();
  if (r.hasCourt === 'Taip' && c) return `Taip, Nr. ${c}`;
  if (r.hasCourt === 'Taip') return 'Taip';
  return 'Ne';
};

const formatPrice = (reservation) => {
  if (reservation.totalPrice != null) {
    const label = reservation.hasCourt !== 'Taip' ? ' (pilna kaina)' : '';
    return `${Number(reservation.totalPrice).toFixed(2)} €${label}`;
  }
  const pricePerHour = getTrainingPricePerHourWithPricing(reservation.numberOfPeople || 1, null);
  const hours = (reservation.duration || 60) / 60;
  return `${(pricePerHour * hours).toFixed(2)} €`;
};

const canCancelReservation = (reservation) => {
  const reservationDateTime = new Date(`${reservation.date}T${reservation.startTime}`);
  const now = new Date();
  const hoursDifference = (reservationDateTime - now) / (1000 * 60 * 60);
  return hoursDifference > 60;
};

export default function ClientVisosRezervacijosPage() {
  const { currentUser } = useAuth();
  const [upcoming, setUpcoming] = useState([]);
  const [past, setPast] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pastLoading, setPastLoading] = useState(false);
  const [showPast, setShowPast] = useState(false);
  const [error, setError] = useState('');
  const [cancelDialog, setCancelDialog] = useState(false);
  const [reservationToCancel, setReservationToCancel] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const userEmail = currentUser?.email || null;

  const loadUpcoming = useCallback(async () => {
    if (!userEmail) {
      setUpcoming([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const list = await getUserFutureReservations(userEmail);
      setUpcoming(list);
    } catch (e) {
      setError(e?.message || 'Nepavyko užkrauti rezervacijų.');
    } finally {
      setLoading(false);
    }
  }, [userEmail]);

  const loadPast = useCallback(async () => {
    if (!userEmail) return;
    setPastLoading(true);
    try {
      const list = await getUserPastReservations(userEmail);
      setPast(list);
    } catch (e) {
      setError(e?.message || 'Nepavyko užkrauti buvusių rezervacijų.');
    } finally {
      setPastLoading(false);
    }
  }, [userEmail]);

  useEffect(() => {
    loadUpcoming();
  }, [loadUpcoming]);

  useEffect(() => {
    if (showPast && past.length === 0 && userEmail) {
      loadPast();
    }
  }, [showPast, userEmail, past.length, loadPast]);

  const handleCancelClick = (reservation) => {
    setReservationToCancel(reservation);
    setCancelDialog(true);
  };

  const handleConfirmCancel = async () => {
    if (!reservationToCancel || cancelling) return;
    setCancelling(true);
    setError('');
    try {
      await cancelReservation(reservationToCancel.id);
      setCancelDialog(false);
      setReservationToCancel(null);
      await loadUpcoming();
    } catch (e) {
      setError(e?.message || 'Nepavyko atšaukti rezervacijos.');
    } finally {
      setCancelling(false);
    }
  };

  const addToCalendar = (reservation) => {
    const locationName = locationLabel(reservation);
    const eventTitle = `Tenisas su ${reservation.coachName || 'treneriu'} ${locationName}`;
    const courtLine = reservation.hasCourt === 'Taip'
      ? (reservation.courtNo || reservation.sebCourtName ? `Aikštelė: Taip, Nr. ${reservation.courtNo || reservation.sebCourtName}` : 'Aikštelė: Taip')
      : 'Aikštelė: Ne';
    const eventDescription = `${courtLine}\nVieta: ${locationName}`;
    const datesParam = vilniusToCalendarUTC(reservation.date, reservation.startTime, reservation.duration);
    const calendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(eventTitle)}&details=${encodeURIComponent(eventDescription)}&dates=${datesParam}`;
    window.open(calendarUrl, '_blank');
  };

  if (!currentUser) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="info">
          Prisijunkite, kad matytumėte savo rezervacijas.
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
        Visos rezervacijos
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Artimiausios viršuje. Galite atšaukti rezervaciją ne vėliau kaip 60 val. iki pamokos.
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Data ir laikas</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Treneris</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Vieta</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Aikštelė</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Kaina</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Būsena</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100', width: 120 }}>Veiksmai</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {upcoming.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  Būsimų rezervacijų nėra.
                </TableCell>
              </TableRow>
            ) : (
              upcoming.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>
                    {format(new Date(r.date + 'T12:00:00'), 'yyyy-MM-dd (EEEE)', { locale: lt })} {r.startTime}–{r.endTime}
                  </TableCell>
                  <TableCell>{r.coachName || '—'}</TableCell>
                  <TableCell>{locationLabel(r)}</TableCell>
                  <TableCell>{courtLabel(r)}</TableCell>
                  <TableCell>{formatPrice(r)}</TableCell>
                  <TableCell>
                    <Chip label={r.status === 'confirmed' ? 'Patvirtinta' : 'Laukia'} color={r.status === 'confirmed' ? 'success' : 'warning'} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      <Button size="small" variant="outlined" startIcon={<EventIcon />} onClick={() => addToCalendar(r)} sx={{ textTransform: 'none', minWidth: 0 }}>
                        Kalendorius
                      </Button>
                      {canCancelReservation(r) && (
                        <Button size="small" variant="outlined" color="error" startIcon={<CancelIcon />} onClick={() => handleCancelClick(r)} sx={{ textTransform: 'none', minWidth: 0 }}>
                          Atšaukti
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
        <Button
          variant="outlined"
          onClick={() => setShowPast(!showPast)}
          endIcon={<KeyboardArrowDownIcon sx={{ transform: showPast ? 'rotate(180deg)' : 'none' }} />}
          disabled={pastLoading}
        >
          {pastLoading ? 'Kraunama…' : showPast ? 'Slėpti buvusias rezervacijas' : `Buvusios rezervacijos${past.length > 0 ? ` (${past.length})` : ''}`}
        </Button>
      </Box>

      {showPast && (
        <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: 'auto', mt: 2 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Data ir laikas</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Treneris</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Vieta</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Aikštelė</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Kaina</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Būsena</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {past.length === 0 && !pastLoading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    Buvusių rezervacijų nėra.
                  </TableCell>
                </TableRow>
              ) : (
                past.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell>
                      {format(new Date(r.date + 'T12:00:00'), 'yyyy-MM-dd (EEEE)', { locale: lt })} {r.startTime}–{r.endTime}
                    </TableCell>
                    <TableCell>{r.coachName || '—'}</TableCell>
                    <TableCell>{locationLabel(r)}</TableCell>
                    <TableCell>{courtLabel(r)}</TableCell>
                    <TableCell>{formatPrice(r)}</TableCell>
                    <TableCell>
                      {r.status === 'cancelled' ? (
                        <Chip label="Atšaukta" size="small" color="error" variant="outlined" />
                      ) : (
                        <Chip label="Įvykdyta" size="small" variant="outlined" />
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={cancelDialog} onClose={() => !cancelling && setCancelDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CancelIcon sx={{ fontSize: 56, color: 'error.main' }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Atšaukti rezervaciją?</Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3, gap: 1.5 }}>
          <Button onClick={() => !cancelling && setCancelDialog(false)} variant="outlined" size="large" disabled={cancelling} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
            Ne
          </Button>
          <Button onClick={handleConfirmCancel} variant="contained" color="error" size="large" disabled={cancelling} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3 }} startIcon={cancelling ? <CircularProgress size={20} sx={{ color: 'inherit' }} /> : null}>
            {cancelling ? 'Atšaukiama...' : 'Taip, atšaukti'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
